"use client";

import {
	classed,
	clsx,
	DialogClose,
	DialogContent,
	DialogRoot,
	DialogTrigger,
	DialogViewport,
} from "@trydig/ui";
import { type ComponentProps, forwardRef } from "react";
import styles from "./Drawer.module.css";

export const DrawerRoot = DialogRoot;

/**
 * Internal: composes the base + side-variant Tailwind classes onto
 * DialogViewport. Consumers should use `DrawerViewport` / `ModalViewport`
 * below — those wrap this with the `lockBodyScroll` API.
 */
const StyledDrawerViewport = classed(DialogViewport, styles.viewportBase, styles.viewport, {
	variants: {
		side: {
			right: styles.right,
			left: styles.left,
		},
	},
});

type DrawerViewportBaseProps = ComponentProps<typeof StyledDrawerViewport>;

export interface DrawerViewportProps extends Omit<DrawerViewportBaseProps, "lock"> {
	/**
	 * Whether the body should be scroll-locked while the drawer is open.
	 * Defaults to `true`. Set to `false` for non-blocking drawers (e.g. an
	 * inline panel where the user should still be able to scroll the page).
	 */
	lockBodyScroll?: boolean;
}

export const DrawerViewport = forwardRef<HTMLDialogElement, DrawerViewportProps>(
	({ lockBodyScroll = true, ...props }, ref) => (
		<StyledDrawerViewport ref={ref} lock={lockBodyScroll} {...props} />
	),
);
DrawerViewport.displayName = "DrawerViewport";

export const DrawerContent = classed(DialogContent, styles.content);
export const DrawerMain = classed("div", styles.main);

export const DrawerClose = ({ inline = false }) => {
	return (
		<DialogClose className={clsx(styles.close, inline && styles.inline)} asChild>
			<button type="button">X</button>
		</DialogClose>
	);
};
export const DrawerTrigger = DialogTrigger;

export const DrawerHeader = classed("header", styles.header, {
	variants: {
		inline: {
			true: styles.inline,
		},
	},
});
export const DrawerFooter = classed("footer", styles.footer);

export const DialogTitle = classed("h2", styles.title, {
	variants: {
		small: {
			true: styles.small,
		},
	},
});
export const DrawerTitle = DialogTitle;
export const ModalTitle = DialogTitle;

export const ModalRoot = DialogRoot;

const StyledModalViewport = classed(DialogViewport, styles.viewportBase, styles.modalViewport);

type ModalViewportBaseProps = ComponentProps<typeof StyledModalViewport>;

export interface ModalViewportProps extends Omit<ModalViewportBaseProps, "lock"> {
	/**
	 * Whether the body should be scroll-locked while the modal is open.
	 * Defaults to `true`. Set to `false` for non-blocking modals.
	 */
	lockBodyScroll?: boolean;
}

export const ModalViewport = forwardRef<HTMLDialogElement, ModalViewportProps>(
	({ lockBodyScroll = true, ...props }, ref) => (
		<StyledModalViewport ref={ref} lock={lockBodyScroll} {...props} />
	),
);
ModalViewport.displayName = "ModalViewport";

export const ModalContent = classed(DialogContent, styles.content);
export const ModalMain = classed("div", styles.main);
export const ModalHeader = classed("header", styles.header);
export const ModalFooter = classed("footer", styles.footer);

export const ModalTrigger = DialogTrigger;

export const ModalClose = () => {
	return (
		<DialogClose className={styles.close} asChild>
			<button type="button">X</button>
		</DialogClose>
	);
};
